import { Phone, Mail, MapPin, Clock, ChevronRight } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const goToSection = (href) => {
    if (location.pathname === '/') {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      try { sessionStorage.setItem('allorep:scrollTo', href); } catch {}
      navigate('/');
    }
  };

  const services = [
    { label: 'Réparation iPhone', to: '/reparation-iphone-montpellier' },
    { label: 'Réparation Samsung', to: '/reparation-samsung-montpellier' },
    { label: 'Micro-soudure', to: '/micro-soudure-montpellier' },
    { label: 'Réparation MacBook', href: '#services' },
    { label: "iPhones d'occasion", href: '#vitrine' },
    { label: 'Rachat iPhone', href: '#devis' },
  ];

  const links = [
    { label: 'Accueil', href: '#hero' },
    { label: 'Services', href: '#services' },
    { label: 'Devis & Rachat', href: '#devis' },
    { label: 'Boutique', href: '#produits' },
    { label: "iPhones d'occasion", href: '#vitrine' },
    { label: 'Avis clients', href: '#avis' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <footer className="bg-slate-900 text-white relative overflow-hidden" data-testid="footer">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

      <div className="absolute top-20 -left-20 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        <div className="py-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
            {/* Brand */}
            <div className="lg:col-span-1">
              <img
                src="/logo.png"
                alt="AlloRéparations Montpellier — Réparation smartphone, tablette, ordinateur"
                width="476"
                height="205"
                loading="lazy"
                className="h-14 w-auto mb-8 brightness-0 invert"
                data-testid="footer-logo"
              />
              <p className="text-slate-400 mb-8 leading-relaxed text-base">
                Expert en réparation de smartphones, tablettes et ordinateurs à Montpellier.
                Service rapide, garanti et transparent.
              </p>

              {/* Social Links */}
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/alloreparations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-600 transition-all duration-300 hover:scale-110"
                  data-testid="social-facebook"
                  aria-label="Facebook AlloRéparations"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/alloreparations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-600 transition-all duration-300 hover:scale-110"
                  data-testid="social-instagram"
                  aria-label="Instagram AlloRéparations"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a
                  href="tel:+33983820754"
                  className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-600 transition-all duration-300 hover:scale-110"
                  data-testid="social-call"
                  aria-label="Appeler la boutique"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-xl font-bold mb-8 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full" />
                Nos services
              </h4>
              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index}>
                    {service.to ? (
                      <Link
                        to={service.to}
                        className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-base"
                        data-testid={`footer-service-${index}`}
                      >
                        <ChevronRight size={16} className="text-slate-600 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                        {service.label}
                      </Link>
                    ) : (
                      <button
                        onClick={() => goToSection(service.href)}
                        className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-base"
                        data-testid={`footer-service-${index}`}
                      >
                        <ChevronRight size={16} className="text-slate-600 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                        {service.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-xl font-bold mb-8 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full" />
                Liens rapides
              </h4>
              <ul className="space-y-4">
                {links.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => goToSection(link.href)}
                      className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-base"
                      data-testid={`footer-link-${index}`}
                    >
                      <ChevronRight size={16} className="text-slate-600 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xl font-bold mb-8 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-full" />
                Contact
              </h4>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-blue-500" size={18} />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Adresse</p>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      7 place des martyrs de la résistance<br />
                      34000 Montpellier
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-green-500" size={18} />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Téléphones</p>
                    <a href="tel:+33983820754" className="block text-slate-400 hover:text-white transition-colors text-sm">
                      <span className="text-slate-500 mr-1">Tél&nbsp;:</span>09 83 82 07 54
                    </a>
                    <a href="sms:+33782373695" className="block text-slate-400 hover:text-white transition-colors text-sm">
                      <span className="text-slate-500 mr-1">SMS&nbsp;:</span>07 82 37 36 95
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-purple-500" size={18} />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Email</p>
                    <a href="mailto:alloreparations@gmail.com" className="text-slate-400 hover:text-white transition-colors text-sm">
                      alloreparations@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-amber-500" size={18} />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Horaires</p>
                    <p className="text-slate-400 text-sm">
                      Lun - Sam : 10h - 20h<br />
                      <span className="text-slate-500">Dimanche : Fermé</span>
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 py-8 pb-24 lg:pb-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-slate-500 text-sm" data-testid="footer-copyright">
              © {currentYear} AlloRéparations. Tous droits réservés.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-sm">
              <Link
                to="/mentions-legales"
                className="text-slate-500 hover:text-white transition-colors"
                data-testid="footer-mentions-legales"
              >
                Mentions légales
              </Link>
              <Link
                to="/politique-confidentialite"
                className="text-slate-500 hover:text-white transition-colors"
                data-testid="footer-confidentialite"
              >
                Politique de confidentialité
              </Link>
              <Link
                to="/cgv"
                className="text-slate-500 hover:text-white transition-colors"
                data-testid="footer-cgv"
              >
                CGV
              </Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('allorep:open-cookie-settings'))}
                className="text-slate-500 hover:text-white transition-colors"
                data-testid="footer-cookies"
              >
                Gérer mes cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
